// src/Register.js
import { useState } from 'react';
import axios from 'axios';
import './App.css';
import logo from './s2e.png';

const API = 'https://snap2excel-backend.onrender.com';

function Register({ onRegister }) {
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [accountType, setAccountType] = useState('personal'); // 'personal' | 'business'
  const [usageMode, setUsageMode] = useState('personal_budget'); // 'personal_budget' | 'self_employed' | 'business_owner'
  const [businessName, setBusinessName] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [role, setRole] = useState('');

  // ✅ NEW: date of birth
  const [dateOfBirth, setDateOfBirth] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // If they choose business, force usageMode = business_owner
      const finalUsageMode =
        accountType === 'business' ? 'business_owner' : usageMode;

      const res = await axios.post(`${API}/api/register`, {
        full_name: fullName,
        email,
        password,
        account_type: accountType,
        business_name: accountType === 'business' ? businessName : '',
        country,
        city,
        role,
        usage_mode: finalUsageMode,

        // ✅ NEW: send DOB to backend
        date_of_birth: dateOfBirth || null,
      });

      if (res.data && res.data.token) {
        onRegister(res.data.token);
      } else {
        setError('Could not register. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Could not register');
    }

    setLoading(false);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'radial-gradient(circle at top left, #e0f2fe, #f8fafc)',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: 380,
          background: 'white',
          padding: '40px 35px',
          borderRadius: 20,
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img
            src={logo}
            alt="Snap2Excel"
            style={{ width: 90, height: 'auto' }}
          />
        </div>

        <h1 style={{ fontSize: 24, marginBottom: 6 }}>Create your account</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 25 }}>
          Start turning your receipts into clean spreadsheets.
        </p>

        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#b91c1c',
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 12,
              width: '100%',
              textAlign: 'center',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            Full name
            <input
              className="auth-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>

          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            Email
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            Password
            <input
              type="password"
              className="auth-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {/* ✅ NEW: Date of birth */}
          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            Date of birth
            <input
              type="date"
              className="auth-input"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </label>

          {/* High-level usage: personal vs business */}
          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            How will you use Snap2Excel?
            <select
              className="auth-input"
              value={accountType}
              onChange={(e) => {
                const value = e.target.value;
                setAccountType(value);
                // adjust usage mode default when switching
                if (value === 'business') {
                  setUsageMode('business_owner');
                } else if (value === 'personal') {
                  // default back to personal budgeting
                  setUsageMode('personal_budget');
                }
              }}
            >
              <option value="personal">For myself / personal expenses</option>
              <option value="business">For my business</option>
            </select>
          </label>

          {accountType === 'personal' && (
            <div
              style={{
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                padding: 12,
                background: '#f8fafc',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#0f172a',
                }}
              >
                What best describes you?
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  marginBottom: 6,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="usageMode"
                  value="personal_budget"
                  checked={usageMode === 'personal_budget'}
                  onChange={() => setUsageMode('personal_budget')}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>Personal / Student</strong> – I want help budgeting
                  my money and keeping track of what I spend.
                </span>
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="usageMode"
                  value="self_employed"
                  checked={usageMode === 'self_employed'}
                  onChange={() => setUsageMode('self_employed')}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>Self-employed / Side hustle</strong> – I need to keep
                  all my receipts and expenses ready for tax and my accountant.
                </span>
              </label>
            </div>
          )}

          {accountType === 'business' && (
            <label
              className="auth-label"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              Business name
              <input
                className="auth-input"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </label>
          )}

          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            Country
            <input
              className="auth-input"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </label>

          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            City
            <input
              className="auth-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </label>

          <label
            className="auth-label"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            What do you do?
            <input
              className="auth-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </label>

          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create account'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <a href="/login" style={{ color: '#2563eb', fontSize: 14 }}>
              Already have an account? Log in
            </a>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Register;
